import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  GlobeHemisphereWest, CurrencyDollar, Translate, MapPin, CaretDown,
  ShieldCheck, Check, FileText
} from '@phosphor-icons/react';

export default function GlobalSettings() {
  const [prefs, setPrefs] = useState({ locale: 'en', region: 'NA', currency: 'USD', timezone: 'UTC' });
  const [taxProfile, setTaxProfile] = useState(null);
  const [consent, setConsent] = useState({ analytics: false, marketing: false, third_party: false, terms_accepted: false });
  const [locales, setLocales] = useState({});
  const [regions, setRegions] = useState({});
  const [currencies, setCurrencies] = useState({});
  const [policies, setPolicies] = useState({});
  const [loading, setLoading] = useState(true);
  const [taxForm, setTaxForm] = useState({ tax_type: 'none', tax_id: '', business_name: '', country: '', vat_registered: false });
  const [showTaxEdit, setShowTaxEdit] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [prefsRes, taxRes, consentRes, locRes, regRes, curRes, polRes] = await Promise.all([
        api.get('/global/preferences'),
        api.get('/global/tax-profile'),
        api.get('/global/consent'),
        api.get('/global/locales'),
        api.get('/global/regions'),
        api.get('/global/currencies'),
        api.get('/global/policies'),
      ]);
      setPrefs(prefsRes.data);
      setTaxProfile(taxRes.data);
      setConsent(consentRes.data);
      setLocales(locRes.data || {});
      setRegions(regRes.data || {});
      setCurrencies(curRes.data || {});
      setPolicies(polRes.data || {});
      if (taxRes.data?.exists) {
        setTaxForm({ tax_type: taxRes.data.tax_type || 'none', tax_id: taxRes.data.tax_id || '', business_name: taxRes.data.business_name || '', country: taxRes.data.country || '', vat_registered: taxRes.data.vat_registered || false });
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const savePrefs = async () => {
    try {
      await api.put('/global/preferences', prefs);
      toast.success('Preferences saved');
    } catch (err) { toast.error('Failed'); }
  };

  const saveTaxProfile = async () => {
    try {
      await api.post('/global/tax-profile', taxForm);
      toast.success('Tax profile saved');
      setShowTaxEdit(false);
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const saveConsent = async () => {
    try {
      await api.put('/global/consent', consent);
      toast.success('Consent preferences saved');
    } catch (err) { toast.error('Failed'); }
  };

  if (loading) return <div className="p-8 text-center text-zinc-500">Loading global settings...</div>;

  const currentRegion = prefs.region && regions[prefs.region] ? regions[prefs.region] : null;
  const currentPolicy = prefs.region && policies[prefs.region] ? policies[prefs.region] : null;

  return (
    <div className="space-y-6" data-testid="global-settings-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Global Settings</h1>
        <p className="text-sm text-zinc-500 mt-1">Region, language, currency, tax, and privacy preferences</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-3"><p className="text-[10px] text-zinc-500 uppercase tracking-wider">Languages</p><p className="text-lg font-bold mt-0.5">{Object.keys(locales).length}</p></CardContent></Card>
        <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-3"><p className="text-[10px] text-zinc-500 uppercase tracking-wider">Regions</p><p className="text-lg font-bold mt-0.5">{Object.keys(regions).length}</p></CardContent></Card>
        <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-3"><p className="text-[10px] text-zinc-500 uppercase tracking-wider">Currencies</p><p className="text-lg font-bold mt-0.5">{Object.keys(currencies).length}</p></CardContent></Card>
        <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-3"><p className="text-[10px] text-zinc-500 uppercase tracking-wider">Tax Status</p><p className="text-lg font-bold mt-0.5">{taxProfile?.exists ? 'Filed' : 'Pending'}</p></CardContent></Card>
      </div>

      {/* Region & Locale Preferences */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><GlobeHemisphereWest size={18} className="text-blue-400" /> Region & Language</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Language</label>
              <div className="relative">
                <select data-testid="pref-locale" value={prefs.locale} onChange={(e) => setPrefs(p => ({...p, locale: e.target.value}))} className="w-full h-9 bg-zinc-800 border border-zinc-700 rounded-md px-3 text-sm text-zinc-300 appearance-none cursor-pointer">
                  {Object.entries(locales).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}
                </select>
                <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Region</label>
              <div className="relative">
                <select data-testid="pref-region" value={prefs.region} onChange={(e) => setPrefs(p => ({...p, region: e.target.value}))} className="w-full h-9 bg-zinc-800 border border-zinc-700 rounded-md px-3 text-sm text-zinc-300 appearance-none cursor-pointer">
                  {Object.entries(regions).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}
                </select>
                <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Currency</label>
              <div className="relative">
                <select data-testid="pref-currency" value={prefs.currency} onChange={(e) => setPrefs(p => ({...p, currency: e.target.value}))} className="w-full h-9 bg-zinc-800 border border-zinc-700 rounded-md px-3 text-sm text-zinc-300 appearance-none cursor-pointer">
                  {Object.entries(currencies).map(([k, v]) => <option key={k} value={k}>{v.symbol} {k} — {v.name}</option>)}
                </select>
                <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Timezone</label>
              <Input data-testid="pref-tz" value={prefs.timezone} onChange={(e) => setPrefs(p => ({...p, timezone: e.target.value}))} className="bg-zinc-800 border-zinc-700" placeholder="UTC" />
            </div>
          </div>
          {currentRegion && currentPolicy && (
            <div className="p-3 rounded-lg bg-zinc-800/30 border border-zinc-800 text-xs text-zinc-400 space-y-1">
              <p><MapPin size={12} className="inline mr-1" /> <strong>{currentRegion.name}</strong> &middot; Countries: {currentRegion.countries?.join(', ')}</p>
              <p>Min Payout: ${currentPolicy.min_payout} &middot; Tax: {currentPolicy.tax_type} &middot; Methods: {currentPolicy.payout_methods?.join(', ')}</p>
            </div>
          )}
          <Button onClick={savePrefs} data-testid="save-prefs-btn" className="bg-blue-600 hover:bg-blue-700">Save Preferences</Button>
        </CardContent>
      </Card>

      {/* Tax Profile */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base flex items-center gap-2"><FileText size={18} className="text-amber-400" /> Tax Profile</CardTitle>
          <Button size="sm" variant="ghost" onClick={() => setShowTaxEdit(!showTaxEdit)} className="text-blue-400">Edit</Button>
        </CardHeader>
        <CardContent>
          {showTaxEdit ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <select value={taxForm.tax_type} onChange={(e) => setTaxForm(p => ({...p, tax_type: e.target.value}))} className="w-full h-9 bg-zinc-800 border border-zinc-700 rounded-md px-3 text-sm text-zinc-300 appearance-none cursor-pointer">
                    <option value="none">None</option><option value="vat">VAT</option><option value="gst">GST</option><option value="sales_tax">Sales Tax</option><option value="withholding">Withholding</option>
                  </select>
                  <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                </div>
                <Input value={taxForm.tax_id} onChange={(e) => setTaxForm(p => ({...p, tax_id: e.target.value}))} placeholder="Tax ID / VAT Number" className="bg-zinc-800 border-zinc-700" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input value={taxForm.business_name} onChange={(e) => setTaxForm(p => ({...p, business_name: e.target.value}))} placeholder="Business name" className="bg-zinc-800 border-zinc-700" />
                <Input value={taxForm.country} onChange={(e) => setTaxForm(p => ({...p, country: e.target.value}))} placeholder="Country" className="bg-zinc-800 border-zinc-700" />
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
                <input type="checkbox" checked={taxForm.vat_registered} onChange={(e) => setTaxForm(p => ({...p, vat_registered: e.target.checked}))} className="rounded bg-zinc-800 border-zinc-700" />
                VAT Registered
              </label>
              <div className="flex gap-2">
                <Button size="sm" onClick={saveTaxProfile} data-testid="save-tax-btn" className="bg-amber-600 hover:bg-amber-700">Save Tax Profile</Button>
                <Button size="sm" variant="ghost" onClick={() => setShowTaxEdit(false)} className="text-zinc-400">Cancel</Button>
              </div>
            </div>
          ) : taxProfile?.exists ? (
            <div className="text-sm text-zinc-400 space-y-1">
              <p>Type: <strong className="text-zinc-300">{taxProfile.tax_type}</strong> &middot; ID: {taxProfile.tax_id || 'N/A'}</p>
              <p>Business: {taxProfile.business_name || 'N/A'} &middot; Country: {taxProfile.country || 'N/A'}</p>
              {taxProfile.vat_registered && <Badge className="bg-emerald-900/30 text-emerald-400 text-[10px]">VAT Registered</Badge>}
            </div>
          ) : (
            <p className="text-sm text-zinc-500">No tax profile set up. Click Edit to configure.</p>
          )}
        </CardContent>
      </Card>

      {/* Privacy & Consent */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><ShieldCheck size={18} className="text-emerald-400" /> Privacy & Consent</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {[
            { key: 'analytics', label: 'Analytics tracking (usage data to improve platform)' },
            { key: 'marketing', label: 'Marketing communications (offers, updates, tips)' },
            { key: 'third_party', label: 'Third-party data sharing (partner integrations)' },
            { key: 'terms_accepted', label: 'Terms of Service and Privacy Policy acceptance' },
          ].map(item => (
            <label key={item.key} className="flex items-center gap-3 text-sm text-zinc-300 cursor-pointer hover:text-zinc-100 transition-colors">
              <input type="checkbox" checked={consent[item.key] || false} onChange={(e) => setConsent(p => ({...p, [item.key]: e.target.checked}))}
                className="rounded bg-zinc-800 border-zinc-700 text-blue-500" data-testid={`consent-${item.key}`} />
              {item.label}
            </label>
          ))}
          <Button onClick={saveConsent} data-testid="save-consent-btn" className="bg-emerald-600 hover:bg-emerald-700">Save Consent Preferences</Button>
        </CardContent>
      </Card>
    </div>
  );
}
